import { useLocale, type MessageKey } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// A DISPUTED PAYMENT, SHOWN WHERE THE PAYMENT IS (#1051).
//
// `GET /bookings` adds `paymentDisputes` to a booking whose payment the
// cardholder disputed. The booking's own `paymentStatus` is left as it is — a
// disputed booking still reads «paid», even once the dispute is lost and the
// payment charged back, because the owner decided the booking is left alone
// (PAYMENTS.md O13, #1066) — so this notice is what tells staff the money is
// contested, or gone.
//
// It only SHOWS. Answering a dispute happens in Stripe's embedded screen under
// Klubbinnstillinger → Betaling, which is the owner's (#946 phase 2).
// ─────────────────────────────────────────────────────────────────────

export interface PaymentDisputeView {
  state: string;
  amountMinor: number;
  currency: string;
  evidenceDueBy: string | null;
  openedAt: string;
  funds: { direction: string; amountMinor: number; feeMinor: number; currency: string }[];
}

const STATE_KEY: Record<string, MessageKey> = {
  INQUIRY_NEEDS_RESPONSE: 'booking.dispute.state.inquiryNeedsResponse',
  INQUIRY_UNDER_REVIEW: 'booking.dispute.state.inquiryUnderReview',
  INQUIRY_CLOSED: 'booking.dispute.state.inquiryClosed',
  NEEDS_RESPONSE: 'booking.dispute.state.needsResponse',
  UNDER_REVIEW: 'booking.dispute.state.underReview',
  WON: 'booking.dispute.state.won',
  LOST: 'booking.dispute.state.lost',
  PREVENTED: 'booking.dispute.state.prevented',
};

/** The states in which the organization still has something to do. */
const AWAITING_RESPONSE = new Set(['INQUIRY_NEEDS_RESPONSE', 'NEEDS_RESPONSE']);

/** Read defensively: the field comes off a booking object typed `any`. */
export function paymentDisputesOf(booking: unknown): PaymentDisputeView[] {
  const list = (booking as { paymentDisputes?: unknown } | null)?.paymentDisputes;
  return Array.isArray(list) ? (list as PaymentDisputeView[]) : [];
}

export function PaymentDisputeNotice({ disputes }: { disputes: PaymentDisputeView[] }) {
  const { t, formatDate, formatNumber } = useLocale();
  if (disputes.length === 0) return null;

  const money = (minor: number, currency: string) =>
    formatNumber(minor / 100, { style: 'currency', currency, minimumFractionDigits: 0 });

  return (
    <div className="space-y-2" data-testid="payment-dispute-notice">
      {disputes.map((d, i) => {
        const withdrawn = d.funds
          .filter((f) => f.direction === 'WITHDRAWN')
          .reduce((sum, f) => sum - f.amountMinor, 0);
        const reinstated = d.funds
          .filter((f) => f.direction === 'REINSTATED')
          .reduce((sum, f) => sum + f.amountMinor, 0);
        const fees = d.funds.reduce((sum, f) => sum + f.feeMinor, 0);
        const fundsCurrency = d.funds[0]?.currency ?? d.currency;
        const lost = d.state === 'LOST';
        return (
          <div
            key={`${d.openedAt}-${i}`}
            role="status"
            className={`rounded border-2 px-3 py-2 text-xs ${
              lost
                ? 'border-red-300 bg-red-50 text-red-800'
                : 'border-amber-300 bg-amber-50 text-amber-900'
            }`}
          >
            <div className="font-semibold">
              {t('booking.dispute.title', {
                state: t(STATE_KEY[d.state] ?? 'booking.dispute.state.unrecognised'),
              })}
            </div>
            <div>
              {t('booking.dispute.amount', {
                amount: money(d.amountMinor, d.currency),
                date: formatDate(new Date(d.openedAt), { dateStyle: 'medium' }),
              })}
            </div>
            {AWAITING_RESPONSE.has(d.state) && d.evidenceDueBy && (
              <div className="font-semibold">
                {t('booking.dispute.dueBy', {
                  date: formatDate(new Date(d.evidenceDueBy), { dateStyle: 'medium' }),
                })}
              </div>
            )}
            {withdrawn > 0 && (
              <div>
                {t('booking.dispute.withdrawn', {
                  amount: money(withdrawn, fundsCurrency),
                  fee: money(fees, fundsCurrency),
                })}
              </div>
            )}
            {reinstated > 0 && (
              <div>
                {t('booking.dispute.reinstated', { amount: money(reinstated, fundsCurrency) })}
              </div>
            )}
            <div className="mt-1 text-[10px] opacity-80">
              {t('booking.dispute.paymentUnchanged')}
            </div>
          </div>
        );
      })}
    </div>
  );
}
